import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";

const AudioContext = createContext(null);

export const useAudio = () => {
  const ctx = useContext(AudioContext);
  if (!ctx) throw new Error("useAudio must be used within AudioProvider");
  return ctx;
};

const SRC = `${process.env.PUBLIC_URL || ""}/audio/beyond-potentials-jingle.mp3`;
const MUTE_KEY = "bp_muted"; // explicit user opt-out

export const AudioProvider = ({ children }) => {
  const audioRef = useRef(null);
  const armedRef = useRef(false);
  const mutedByUserRef = useRef(false);
  const [enabled, setEnabled] = useState(false);

  // Lazily create a single persistent audio element
  if (audioRef.current === null && typeof Audio !== "undefined") {
    const a = new Audio(SRC);
    a.loop = true;
    a.preload = "auto";
    a.volume = 0.55;
    audioRef.current = a;
  }

  // restore explicit mute preference
  useEffect(() => {
    try { mutedByUserRef.current = localStorage.getItem(MUTE_KEY) === "1"; } catch (e) {}
  }, []);

  const play = useCallback(async () => {
    const a = audioRef.current;
    if (!a) return false;
    try { await a.play(); return true; } catch (e) { return false; }
  }, []);

  const enableSound = useCallback(async () => {
    mutedByUserRef.current = false;
    try { localStorage.setItem(MUTE_KEY, "0"); } catch (e) {}
    setEnabled(true);
    return play();
  }, [play]);

  const disableSound = useCallback(() => {
    mutedByUserRef.current = true;
    try { localStorage.setItem(MUTE_KEY, "1"); } catch (e) {}
    setEnabled(false);
    const a = audioRef.current;
    if (a) a.pause();
  }, []);

  const toggle = useCallback(() => {
    if (enabled) disableSound();
    else enableSound();
  }, [enabled, enableSound, disableSound]);

  // Arm global "first interaction" auto-play. Called once the intro gate is dismissed.
  const armAutoplay = useCallback(() => {
    if (armedRef.current) return;
    armedRef.current = true;

    const events = ["pointerdown", "touchstart", "keydown", "wheel", "click", "scroll"];
    const handler = async () => {
      const a = audioRef.current;
      if (mutedByUserRef.current) { cleanup(); return; }
      if (a && a.paused) {
        const ok = await play();
        if (ok) { setEnabled(true); cleanup(); }
      } else {
        cleanup();
      }
    };
    const cleanup = () => {
      events.forEach((ev) => window.removeEventListener(ev, handler));
    };
    events.forEach((ev) => window.addEventListener(ev, handler, { passive: true }));
  }, [play]);

  // Pause when tab hidden; resume if enabled
  useEffect(() => {
    const onVis = () => {
      const a = audioRef.current;
      if (!a) return;
      if (document.hidden) a.pause();
      else if (enabled && !mutedByUserRef.current) a.play().catch(() => {});
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [enabled]);

  const value = useMemo(
    () => ({ enabled, enableSound, disableSound, toggle, armAutoplay }),
    [enabled, enableSound, disableSound, toggle, armAutoplay]
  );

  return <AudioContext.Provider value={value}>{children}</AudioContext.Provider>;
};
